import { IconButton } from '@mui/material';
import moment from 'moment';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import EditIcon from '@mui/icons-material/Edit';
import CustomModal from '../../Modals/Modals';
import styles from './TopBar.module.css';
import { ReactComponent as PendingActionIcon } from '../../../../../Assets/icons/pendingaction.svg';
import { Card, CardBody, CardHeader } from '../../Card/Card';
import Badge from '../../Badge/Badge';
import ReviewApplicationModal from './ReviewApplicationModal';
import { ApplicationStatus } from '../../../../../Lib/Constants/vendor-application-form-step';

function DisplayBadgeStatus({ status }) {
  let color = '';
  switch (status) {
    case 'Pending':
      color = 'neutral';
      break;
    case 'In Progress':
      color = 'yellow';
      break;
    case 'Completed':
      color = 'green';
      break;
    case 'Approved':
      color = 'green';
      break;
    case 'Denied':
      color = 'red';
      break;
    default:
      color = 'neutral';
  }

  return <Badge color={color} label={status} />;
}

DisplayBadgeStatus.propTypes = {
  status: PropTypes.string.isRequired,
};

function getApprovalLabel(status) {
  return status === 'Denied' ? 'Denied By' : 'Approved By';
}

function ApprovalCard({ title, data, openModal = () => {}, editable = false }) {
  return (
    <Card noShadow>
      <CardHeader>{title}</CardHeader>
      <CardBody>
        <div className={styles.twoColumnLayout}>
          <div className={styles.labelColumn}>Status</div>
          <div className={styles.valueColumn}>
            <DisplayBadgeStatus status={data.status} />
            {editable && (
              <IconButton
                color="primary"
                aria-label="add an alarm"
                onClick={openModal}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        </div>
        {data.approved_by && (
          <div className={styles.twoColumnLayout}>
            <div className={styles.labelColumn}>
              {getApprovalLabel(data.status)}
            </div>
            <div className={styles.valueColumn}>{data.approved_by}</div>
          </div>
        )}
        {data.date && (
          <div className={styles.twoColumnLayout}>
            <div className={styles.labelColumn}>Date</div>
            <div className={styles.valueColumn}>
              {moment(data.date).format('MM-DD-YYYY, h:mm:ss')}
            </div>
          </div>
        )}
        {data.updated_status && (
          <div className={styles.twoColumnLayout}>
            <div className={styles.labelColumn}>Updated Status</div>
            <div className={styles.valueColumn}>
              <DisplayBadgeStatus status={data.updated_status} />
            </div>
          </div>
        )}
        {data.updated_by && (
          <div className={styles.twoColumnLayout}>
            <div className={styles.labelColumn}>Updated By</div>
            <div className={styles.valueColumn}>{data.updated_by}</div>
          </div>
        )}
        {data.updated_at && (
          <div className={styles.twoColumnLayout}>
            <div className={styles.labelColumn}>Updated At</div>
            <div className={styles.valueColumn}>
              {moment(data.updated_at).format('MM-DD-YYYY, h:mm:ss')}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

ApprovalCard.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.shape({
    status: PropTypes.string,
    approved_by: PropTypes.string,
    date: PropTypes.string,
  }).isRequired,
  openModal: PropTypes.func,
  editable: PropTypes.bool,
};

function StatusPanelModal({
  status,
  currentApplication,
  onApprove,
  onCancelApprove,
  userRole,
}) {
  const [openStatusPanel, setOpenStatusPanel] = React.useState(false);
  const [openApprovalModal, setOpenApprovalModal] = React.useState(false);

  const metadata = useMemo(
    () => currentApplication.metadata || {},
    [currentApplication.metadata],
  );

  return (
    <>
      <ReviewApplicationModal
        open={openApprovalModal}
        onClose={() => setOpenApprovalModal(false)}
        confirmationText="approve"
        status={status}
        currentApplication={currentApplication}
        onApprove={onApprove}
        onCancelApprove={() => onCancelApprove('approve')}
      />
      <IconButton
        className={styles.statusButton}
        aria-label="show status panel"
        onClick={() => setOpenStatusPanel(true)}
        size="large"
      >
        <p>Status</p>
        <PendingActionIcon />
      </IconButton>
      <CustomModal
        open={openStatusPanel}
        handleClose={() => setOpenStatusPanel(false)}
      >
        <div className={styles.modalBody}>
          <div>
            <div className={styles.modalTitle}>Status</div>
            <div className={styles.modalText}>
              {metadata.vendorQuestionnaire && (
                <Card noShadow>
                  <CardHeader>Application</CardHeader>
                  <CardBody>
                    <div className={styles.twoColumnLayout}>
                      <div className={styles.labelColumn}>Status</div>
                      <div className={styles.valueColumn}>
                        <DisplayBadgeStatus
                          status={metadata.vendorQuestionnaire.status}
                        />
                      </div>
                    </div>
                    <div className={styles.twoColumnLayout}>
                      <div className={styles.labelColumn}>Requester Name</div>
                      <div className={styles.valueColumn}>
                        {metadata.vendorQuestionnaire.requesterName}
                      </div>
                    </div>
                    <div className={styles.twoColumnLayout}>
                      <div className={styles.labelColumn}>Sponsor Name</div>
                      <div className={styles.valueColumn}>
                        {metadata.vendorQuestionnaire.sponsor_name}
                      </div>
                    </div>
                    {metadata.vendorQuestionnaire.date && (
                      <div className={styles.twoColumnLayout}>
                        <div className={styles.labelColumn}>Date</div>
                        <div className={styles.valueColumn}>
                          {moment(metadata.vendorQuestionnaire.date).format(
                            'MM-DD-YYYY, h:mm:ss',
                          )}
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
              {metadata.buyerQuestionnaire && (
                <Card noShadow>
                  <CardHeader>Buyer Questionnaire</CardHeader>
                  <CardBody>
                    <div className={styles.twoColumnLayout}>
                      <div className={styles.labelColumn}>Status</div>
                      <div className={styles.valueColumn}>
                        <DisplayBadgeStatus
                          status={metadata.buyerQuestionnaire.status}
                        />
                      </div>
                    </div>
                    {metadata.buyerQuestionnaire.updated_by && (
                      <div className={styles.twoColumnLayout}>
                        <div className={styles.labelColumn}>Updated By</div>
                        <div className={styles.valueColumn}>
                          {metadata.buyerQuestionnaire.updated_by}
                        </div>
                      </div>
                    )}
                    {metadata.buyerQuestionnaire.date && (
                      <div className={styles.twoColumnLayout}>
                        <div className={styles.labelColumn}>Date</div>
                        <div className={styles.valueColumn}>
                          {moment(metadata.buyerQuestionnaire.date).format(
                            'MM-DD-YYYY, h:mm:ss',
                          )}
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
              {metadata.sponsorApproval && (
                <ApprovalCard
                  title="Sponsor Review"
                  data={metadata.sponsorApproval}
                  editable={
                    !currentApplication.application_archived_at &&
                    status === ApplicationStatus.SPONSOR_DENIED &&
                    userRole === 'EXECUTIVE'
                  }
                  openModal={() => setOpenApprovalModal(true)}
                />
              )}
              {metadata.firstExecutiveApproval && (
                <ApprovalCard
                  title="First Executive Review"
                  data={metadata.firstExecutiveApproval}
                />
              )}
              {metadata.secondExecutiveApproval && (
                <ApprovalCard
                  title="Second Executive Review"
                  data={metadata.secondExecutiveApproval}
                />
              )}
              {currentApplication.status ===
                ApplicationStatus.PRE_ONBOARDING && (
                <Card noShadow>
                  <CardHeader>Pre-Onboarding</CardHeader>
                  <CardBody>
                    <div className={styles.twoColumnLayout}>
                      <div className={styles.labelColumn}>Status</div>
                      <div className={styles.valueColumn}>
                        <DisplayBadgeStatus
                          status={currentApplication.vendor_application_status}
                        />
                      </div>
                    </div>
                    {currentApplication.vendor_application_status ===
                      'Pending Accounting Approval' && (
                      <div className={styles.twoColumnLayout}>
                        <div className={styles.labelColumn}>Submitted At</div>
                        <div className={styles.valueColumn}>
                          {moment(
                            currentApplication.vendor_submitted_at,
                          ).format('MM-DD-YYYY, h:mm:ss')}
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
}

StatusPanelModal.propTypes = {
  status: PropTypes.string.isRequired,
  currentApplication: PropTypes.shape({
    vendor_name: PropTypes.string,
    metadata: PropTypes.shape({
      vendorQuestionnaire: PropTypes.shape({
        status: PropTypes.string,
        requesterName: PropTypes.string,
        sponsor_name: PropTypes.string,
        date: PropTypes.string,
      }),
      buyerQuestionnaire: PropTypes.shape({
        status: PropTypes.string,
        updated_by: PropTypes.string,
        date: PropTypes.string,
      }),
      sponsorApproval: PropTypes.shape({
        status: PropTypes.string,
        approved_by: PropTypes.string,
        date: PropTypes.string,
      }),
      firstExecutiveApproval: PropTypes.shape({
        status: PropTypes.string,
        approved_by: PropTypes.string,
        date: PropTypes.string,
      }),
      secondExecutiveApproval: PropTypes.shape({
        status: PropTypes.string,
        approved_by: PropTypes.string,
        date: PropTypes.string,
      }),
    }),
  }).isRequired,
  onApprove: PropTypes.func.isRequired,
  onCancelApprove: PropTypes.func.isRequired,
  userRole: PropTypes.string.isRequired,
};

export { StatusPanelModal };
